'use es6';

import { ENUMERATION } from 'customer-data-objects/property/PropertyTypes';
import { List, is, OrderedSet } from 'immutable';
import Any from 'customer-data-filters/filterQueryFormat/operator/Any';
import FilterEditorOperator from './editor/FilterEditorOperator';
import FilterOperatorDisplay from './operator/FilterOperatorDisplay';
import FilterOperatorType from './propTypes/FilterOperatorType';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedList from 'I18n/components/FormattedList';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import KnowledgeBaseButton from 'ui-addon-i18n/components/KnowledgeBaseButton';
import MissingField from 'customer-data-filters/filterQueryFormat/MissingField';
import PropTypes from 'prop-types';
import { Component } from 'react';
import Small from 'UIComponents/elements/Small';
import UIFlex from 'UIComponents/layout/UIFlex';
import classNames from 'classnames';
import get from 'transmute/get';
import memoize from 'transmute/memoize';
import partial from 'transmute/partial';
import AppleEmailPrivacyWarning from './AppleEmailPrivacyWarning';
import ConstantFilterWarning from './ConstantFilterWarning';
import DeprecatedFilter from '../filterQueryFormat/operator/DeprecatedFilter';
import { isExternalOptionsField } from '../utilities/isExternalOptionsField';
import { isExternalOptionsEnum } from '../utilities/isExternalOptionsEnum';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
class FilterEditorOperatorDisplay extends Component {
  constructor() {
    super();

    // In new code, please use ES6 spread args rather than partials
    // for easier debugging and readability:
    // (...args) => myFunction('partiallyAppliedParam', ...args)
    this.getInvalidOptions = (field, operator, getFamilyValueResolver) => {
      const fieldResolver = getFamilyValueResolver(operator);
      if (field.type === ENUMERATION && field.options && field.options.size > 0 && !fieldResolver && operator.constructor.isIterableField('value')) {
        return operator.value.filter(value => field.options.findIndex(option => get('value', option) === value) === -1);
      }
      return List();
    };
    this.getExternalOptionsFieldOperators = field => {
      const {
        getOperatorsForType,
        getIsUngated,
        filterFamily
      } = this.props;
      const {
        type
      } = field;
      const fieldTypeOperators = getOperatorsForType(type, filterFamily, getIsUngated);
      const enumOperators = getOperatorsForType(ENUMERATION, filterFamily, getIsUngated);
      return OrderedSet.of(...enumOperators, ...fieldTypeOperators);
    };
    this.partial = memoize(partial);
  }
  shouldComponentUpdate(prevProps) {
    return Object.keys(this.props).some(key => {
      if (key === 'path' || key === 'operator') {
        if (!is(this.props[key], prevProps[key])) {
          return true;
        }
      } else {
        if (this.props[key] !== prevProps[key]) {
          return true;
        }
      }
      return false;
    });
  }
  getErrorMessage(operator, operatorLabel, validOperators, getFamilyValueResolver, showInvalidOptionErrors, validateOperator) {
    const field = operator.get('field');
    const isValidOperator = operator.constructor === DeprecatedFilter || MissingField.isMissingField(field) || operator && validOperators.has(operator.constructor);
    const isMissingFieldWithError = MissingField.isMissingField(field) && field.errorMessage;
    const invalidOptions = this.getInvalidOptions(field, operator, getFamilyValueResolver);
    const operatorValidationError = validateOperator(operator);
    if (operatorValidationError.error && operatorValidationError.message) {
      return operatorValidationError.message;
    }
    if (isMissingFieldWithError) {
      return field.errorMessage;
    } else if (!isValidOperator) {
      return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "customerDataFilters.FilterEditorOperatorDisplayList.invalidOperator",
        options: {
          operatorType: operatorLabel
        }
      });
    } else if (showInvalidOptionErrors && invalidOptions.size > 0) {
      return /*#__PURE__*/_jsxs(UIFlex, {
        direction: "column",
        children: [/*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "customerDataFilters.FilterEditorOperatorDisplayList.invalidOption.error",
          options: {
            count: invalidOptions.size,
            fieldName: field.label,
            invalidOptions: this.renderInvalidOptions(invalidOptions)
          }
        }), /*#__PURE__*/_jsx(KnowledgeBaseButton, {
          url: "https://knowledge.hubspot.com/crm-setup/resolve-invalid-filter-errors",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataFilters.FilterEditorOperatorDisplayList.invalidOption.help"
          })
        })]
      });
    }
    return null;
  }
  renderInvalidOptions(invalidOptions) {
    return /*#__PURE__*/_jsx(FormattedList, {
      exclusive: false,
      children: invalidOptions.map(value => /*#__PURE__*/_jsx("b", {
        children: value
      }, value))
    });
  }

  // for external options enabled fields, validOperators = field type operators + enum operators

  render() {
    const {
      currencyCode,
      filterFamily,
      getFieldDefinitions,
      getIsUngated,
      getLabelString,
      getOperators,
      getOperatorsForType,
      getOperatorLabel,
      getFilterFamilyObjectName,
      getReferencedObjectType,
      getSpecialOptionsForReferenceType,
      getValueResolver,
      handleOpenEdit,
      handleOperatorRemove,
      isReadOnly,
      isXoEnabled,
      isZeroLevel,
      operator,
      path,
      showInvalidOptionErrors,
      validateOperator,
      includeObjectsWithNoAssociatedObjects
    } = this.props;
    const isTopLevel = path.size <= 2;
    const isRefinementPresent = operator && operator.constructor.isRefinable && operator.refinement && operator.refinement.constructor !== Any;
    const getFamilyValueResolver = this.partial(getValueResolver, filterFamily);
    const field = operator.get('field');
    const sharedProps = {
      currencyCode,
      getIsUngated,
      fieldDefinitions: getFieldDefinitions(filterFamily),
      getFamilyValueResolver,
      getLabelString,
      getReferencedObjectType: this.partial(getReferencedObjectType, filterFamily),
      getSpecialOptionsForReferenceType,
      hideRestrictedAsset: true,
      isReadOnly,
      isXoEnabled,
      includeObjectsWithNoAssociatedObjects,
      getFilterFamilyObjectName,
      filterFamily
    };
    const isExternalOptionsEnabledField = isExternalOptionsEnum(field) && isExternalOptionsField(field, filterFamily);
    const validOperators = isExternalOptionsEnabledField && getOperatorsForType ? this.getExternalOptionsFieldOperators(field) : getOperators(field, filterFamily);
    const operatorLabel = getOperatorLabel(operator.constructor);
    const errorMessage = this.getErrorMessage(operator, operatorLabel, validOperators, getFamilyValueResolver, showInvalidOptionErrors, validateOperator);
    return /*#__PURE__*/_jsxs(FilterEditorOperator, {
      className: classNames(isXoEnabled && !isZeroLevel && ['p-left-4 p-right-2 ', isTopLevel && 'p-y-3', !isTopLevel && 'p-y-1']),
      hoverContentClassName: isXoEnabled && isTopLevel ? 'p-top-2' : "",
      isReadOnly: isReadOnly,
      isXoEnabled: isXoEnabled,
      onEdit: this.partial(handleOpenEdit, path, filterFamily),
      onRemove: this.partial(handleOperatorRemove, path),
      operator: operator,
      children: [!isRefinementPresent && /*#__PURE__*/_jsx(FilterOperatorDisplay, Object.assign({}, sharedProps, {
        operator: operator
      })), isRefinementPresent && /*#__PURE__*/_jsxs("span", {
        children: [/*#__PURE__*/_jsx(FilterOperatorDisplay, Object.assign({}, sharedProps, {
          operator: operator
        })), /*#__PURE__*/_jsx(FilterOperatorDisplay, Object.assign({}, sharedProps, {
          operator: operator.refinement
        }))]
      }), errorMessage && /*#__PURE__*/_jsx(Small, {
        className: "display-block",
        use: "error",
        children: errorMessage
      }), /*#__PURE__*/_jsx(ConstantFilterWarning, {
        condition: operator
      }), /*#__PURE__*/_jsx(AppleEmailPrivacyWarning, {
        condition: operator,
        getIsUngated: getIsUngated,
        isReadOnly: isReadOnly
      })]
    });
  }
}
FilterEditorOperatorDisplay.propTypes = {
  currencyCode: PropTypes.string.isRequired,
  filterFamily: PropTypes.string.isRequired,
  getFieldDefinitions: PropTypes.func.isRequired,
  getFilterFamilyObjectName: PropTypes.func,
  getIsUngated: PropTypes.func,
  getLabelString: PropTypes.func.isRequired,
  getOperatorLabel: PropTypes.func.isRequired,
  getOperators: PropTypes.func.isRequired,
  getOperatorsForType: PropTypes.func,
  getReferencedObjectType: PropTypes.func.isRequired,
  getSpecialOptionsForReferenceType: PropTypes.func.isRequired,
  getValueResolver: PropTypes.func.isRequired,
  handleOpenEdit: PropTypes.func.isRequired,
  handleOperatorRemove: PropTypes.func.isRequired,
  includeObjectsWithNoAssociatedObjects: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  isXoEnabled: PropTypes.bool.isRequired,
  isZeroLevel: PropTypes.bool,
  operator: FilterOperatorType.isRequired,
  path: PropTypes.instanceOf(List),
  showInvalidOptionErrors: PropTypes.bool.isRequired,
  validateOperator: PropTypes.func.isRequired
};
export default FilterEditorOperatorDisplay;